import styled, { css } from 'styled-components'

import { InlineLinkWrapper } from '@/components/InlineLink/InlineLink.styles'
import { Cover, Reset, TransitionPrimary } from '@/lib/mixins'

export const PaymentMethodWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
`

export const PaymentMethodFooter = styled.div`
  margin-top: auto;
  padding-top: 1.25rem;
`

export const PaymentMethodTitle = styled.h2`
  text-align: center;
  max-width: 10em;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${props => props.theme.spacing.sm};
`

export const PaymentMethodOptions = styled.div`
  display: flex;

  > * {
    flex: 1 0 0;

    &:not(:last-child) {
      margin-right: 1.5rem;
    }
  }
`

export const PaymentOptionIcons = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  ${props =>
    props.theme.contrastMode &&
    css`
      display: none;
    `};

  img {
    flex-shrink: 0;
    object-fit: contain;
    max-height: 2.25rem;
    max-width: 4rem;

    &:not(:last-child) {
      margin-right: 1.5rem;
    }
  }
`

export const PaymentOptionContent = styled.div`
  position: relative;
  z-index: 2;

  h4 {
    font-size: 1.5rem;
    margin-bottom: 0;
  }
`

interface PaymentOptionWrapperProps {
  active?: boolean
  vertical?: boolean
}

export const PaymentOptionWrapper = styled.button<PaymentOptionWrapperProps>`
  ${Reset('button')}
  width: 100%;
  position: relative;
  display: flex;
  height: 10rem;
  align-items: center;
  border: 1px solid ${props => props.theme.color.line};
  padding: 0 8%;

  ${props =>
    props.vertical &&
    css`
      flex-direction: column;
      padding: 2rem;
      align-items: stretch;
      text-align: left;

      &:not(:last-child) {
        margin-bottom: 0;
      }

      ${PaymentOptionIcons} {
        margin-left: 0;
        margin-top: auto;
      }
    `};

  &:after {
    ${Cover('-1px')}
    border: 9px solid ${props => props.theme.color.secondary};
    ${TransitionPrimary('transform, opacity, visibility')}
    visibility: hidden;
    opacity: 0;
    transform: scale(1.05);

    ${props =>
      props.theme.contrastMode &&
      css`
        background-color: ${props => props.theme.color.secondary};
      `};
  }

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }

  ${props =>
    props.active &&
    css`
      color: ${props => props.theme.color.dark};

      &:after {
        visibility: visible;
        opacity: 1;
        transform: scale(1);
      }
    `};
`

export const PaymentTaxIdWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 0;
  width: 100%;

  ${props =>
    props.theme.accessibilityMode &&
    css`
      align-items: stretch;
      padding: 1.5rem 0;
      gap: 1rem;
      ${InlineLinkWrapper} {
        margin-left: 0;
        margin-right: 0;
      }
    `}

  p {
    font-size: 1.1rem;
    font-weight: 500;
    margin-bottom: 1.5rem;

    ${props =>
      props.theme.accessibilityMode &&
      css`
        margin-bottom: 0rem;
        max-width: 12em;
        margin-right: auto;
      `}
  }

  strong {
    font-size: 2.25rem;
    font-weight: 600;
    letter-spacing: 0.4em;
    display: block;
    margin-right: -0.25em;

    ${props =>
      props.theme.accessibilityMode &&
      css`
        font-size: 1.5rem;
      `}
  }

  ${InlineLinkWrapper} {
    &:not(:last-child) {
      margin-right: 2.5rem;
    }
  }

  ${props =>
    props.theme.accessibilityMode &&
    css`
      flex-wrap: wrap;
      min-height: 6rem;
    `};
`

export const PaymentTaxIdSummary = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 2rem;
  text-align: left;
`

export const PaymentTaxIdContent = styled.div`
  min-width: 0;

  p {
    margin: 0 0 0.5rem;
    color: ${props => props.theme.color.muted};
    font-size: 1.1rem;
    font-weight: 400;
  }

  strong {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 400;
    letter-spacing: 0;
  }

  b {
    font-weight: 700;
  }
`

export const PaymentTaxIdButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;

  ${props =>
    props.theme.accessibilityMode &&
    css`
      margin-top: 0;
      margin-left: 2rem;
    `};
`

export const PaymentInfo = styled.div`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1.5rem;
  text-align: center;
  margin-top: auto;
  font-weight: 500;

  ${props =>
    props.theme.accessibilityMode &&
    css`
      position: relative;
      left: 0;
      right: 0;
      bottom: 0;
      text-align: left;
    `};
`
